import * as React from "react";
import { getExchangeLogo } from "@cede/utils";
import { BaseIcon, BaseIconProps } from "@/components/icons/primitives/BaseIcon";

export interface ExchangeIconProps extends Omit<BaseIconProps, "children"> {
  /**
   * Exchange name (e.g., 'binance', 'kucoin', 'okx')
   */
  exchange: string;

  /**
   * Fallback icon to display if logo fails to load
   */
  fallback?: React.ReactNode;

  /**
   * Whether to show exchange name as fallback
   * @default true
   */
  showNameFallback?: boolean;
}

/**
 * Exchange icon component that automatically resolves exchange logos
 *
 * Provides a unified interface for displaying exchange/wallet icons with
 * automatic logo URL resolution and fallback handling.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ExchangeIcon exchange="binance" size="md" />
 *
 * // With underlay background
 * <ExchangeIcon
 *   exchange="kucoin"
 *   size="lg"
 *   withUnderlay
 *   underlayClassName="bg-green-500"
 * />
 *
 * // Interactive with badge
 * <ExchangeIcon
 *   exchange="okx"
 *   interactive
 *   badge={3}
 *   onClick={() => handleClick()}
 * />
 *
 * // With custom fallback
 * <ExchangeIcon
 *   exchange="unknown"
 *   fallback={<div>?</div>}
 * />
 * ```
 */
export const ExchangeIcon = React.forwardRef<SVGSVGElement, ExchangeIconProps>(
  ({ exchange, fallback, showNameFallback = true, ...baseIconProps }, ref) => {
    const [imageError, setImageError] = React.useState(false);
    const logoUrl = getExchangeLogo(exchange);
    const shouldShowFallback = imageError || !logoUrl;

    // Reset error state when exchange changes
    React.useEffect(() => {
      setImageError(false);
    }, [exchange]);

    // If image failed to load or logo is missing, show fallback
    if (shouldShowFallback) {
      if (fallback) {
        return (
          <BaseIcon ref={ref} {...baseIconProps}>
            {fallback}
          </BaseIcon>
        );
      }

      // Default fallback: first letter of exchange name
      if (showNameFallback) {
        const fallbackLetter = exchange.charAt(0).toUpperCase();
        return (
          <BaseIcon ref={ref} {...baseIconProps}>
            <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize="12" fontWeight="600" fill="currentColor">
              {fallbackLetter}
            </text>
          </BaseIcon>
        );
      }
    }

    return (
      <BaseIcon ref={ref} {...baseIconProps}>
        <image
          href={logoUrl || ""}
          width="24"
          height="24"
          onError={() => setImageError(true)}
          aria-label={`${exchange} logo`}
        />
      </BaseIcon>
    );
  },
);

ExchangeIcon.displayName = "ExchangeIcon";
